import React, { useCallback, useEffect, useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { invitationsApi, type OpenInvitation } from '../../api/services';
import { setupCopy } from '../../copy/setup';
import { fill } from './setupParts';
import { trackSetup } from './telemetry';

// The Organisation page's EA invite links (GUIDED_SETUP_BUILD_SPEC §7.2):
// the open ones with Revoke, and a new link for any executive.

const c = setupCopy.invite;

function day(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

export function OpenInvitesCard({ executives }: { executives: Array<{ id: string; displayName: string }> }) {
  const { toast } = useToast();
  const [invites, setInvites] = useState<OpenInvitation[] | null>(null);
  const [executiveId, setExecutiveId] = useState('');
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<string | null>(null);

  const load = useCallback(() => {
    invitationsApi.list().then(setInvites).catch(() => setInvites([]));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (!executiveId && executives.length) setExecutiveId(executives[0].id);
  }, [executives, executiveId]);

  const make = async () => {
    if (busy || !executiveId) return;
    setBusy(true);
    try {
      const { url } = await invitationsApi.create(executiveId);
      trackSetup('setup_invite_created', { where: 'organisation' });
      try {
        if (!navigator.clipboard) throw new Error('no clipboard');
        await navigator.clipboard.writeText(url);
        setShown(null);
        toast({ variant: 'success', message: c.copied });
      } catch {
        setShown(url);
      }
      load();
    } catch (e: any) {
      toast({ variant: 'error', message: e?.response?.data?.error || c.failed });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await invitationsApi.revoke(id);
      setInvites((list) => list?.filter((i) => i.id !== id) ?? null);
      toast({ variant: 'success', message: c.revoked });
    } catch {
      toast({ variant: 'error', message: c.revokeFailed });
    }
  };

  return (
    <Card className="mt-3">
      <CardContent className="pt-6">
        <h3 className="text-base font-semibold">{c.openTitle}</h3>
        <p className="mb-3 text-sm text-muted-foreground">{c.openSub}</p>

        {invites === null ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading" />
        ) : invites.length === 0 ? (
          <p className="text-sm text-muted-foreground">{c.none}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {invites.map((i) => (
              <li key={i.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{fill(c.forExec, { name: i.executiveName ?? '' })}</p>
                  <p className="text-[12.5px] text-muted-foreground">{fill(c.madeBy, { name: i.createdByName ?? '', when: day(i.expiresAt) })}</p>
                </div>
                <Button size="sm" variant="outline" className="h-8" aria-label={`${c.revoke}: ${fill(c.forExec, { name: i.executiveName ?? '' })}`}
                  onClick={() => revoke(i.id)}>{c.revoke}</Button>
              </li>
            ))}
          </ul>
        )}

        {executives.length > 0 && (
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
            <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-[12.5px] text-muted-foreground">
              {c.pickExec}
              <select value={executiveId} onChange={(e) => setExecutiveId(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground">
                {executives.map((e) => <option key={e.id} value={e.id}>{e.displayName}</option>)}
              </select>
            </label>
            <Button size="sm" className="h-9 gap-1.5" onClick={make} disabled={busy || !executiveId}>
              {busy ? <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden /> : <Link2 className="h-3.5 w-3.5" aria-hidden />}
              {c.newLink}
            </Button>
            {shown && (
              <div className="w-full">
                <p className="text-[12px] text-muted-foreground">{c.shownOther}</p>
                <input readOnly value={shown} aria-label={c.newLink} onFocus={(e) => e.currentTarget.select()}
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 font-plex text-[12px]" />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
